import { observer } from 'mobx-react-lite';
import { Box, Tab } from '@cad-arc';
import { NotificationCounter } from '../../../common/components/notification-counter';
import { useApplicationType } from '../../../common/hooks/use-application-type';
import { getShortLabel } from '../../helpers/conversation-participant-helpers';
import { closeAndClearActiveFilterTabMenu } from '../../helpers/conversations-filter-helpers';
import { useMessagingStore } from '../../hooks/use-messaging-store';
import { useNewConversationCancellationModal } from '../../hooks/use-new-conversation-cancellation-modal';
import { buildParticipantKey } from '../../store/helpers';
import { ConversationsFilter } from '../../types';
import styles from './filter-tab.module.css';

export const ConversationFilterTab: React.FC<{
    filter: ConversationsFilter | undefined;
    setShowAllConversations?: (value: boolean) => void;
}> = observer(({ filter, setShowAllConversations }) => {
    const {
        participantFiltersInfo,
        setSelectedConversationFilter,
        setActiveFilterTabMenu,
        activeConversationsUnreadInfo,
        isCreatingNewConversation,
        isBookmarksView,
    } = useMessagingStore();
    const {
        setIsOpen,
        updateConfirmCancellationStoredData,
    } = useNewConversationCancellationModal();
    const { isFirstResponder } = useApplicationType();

    if (
        filter === undefined ||
        participantFiltersInfo === undefined ||
        activeConversationsUnreadInfo === undefined
    ) {
        // don't show filter button if user doesn't have this participant identity type or data not loaded
        return <></>;
    }

    const { filterType, filterParticipant } = filter;
    const filterUnreadInfo =
        filterType === 'ALL_ACTIVE'
            ? activeConversationsUnreadInfo
            : participantFiltersInfo[
                  buildParticipantKey(filterParticipant.id, filterParticipant.entityType)
              ];
    const { unreadCount = 0, hasUrgentUnread = false } = filterUnreadInfo ?? {};

    return (
        <Tab
            isDisabled={isBookmarksView}
            data-testid="conversation-filter-tab"
            value={filterType}
            tabIndex={-1}
            onClick={() => {
                if (isFirstResponder && setShowAllConversations) {
                    setShowAllConversations(true);
                }
                closeAndClearActiveFilterTabMenu({
                    filterType,
                    setActiveFilterTabMenu,
                });
                if (isCreatingNewConversation) {
                    updateConfirmCancellationStoredData?.(filter);
                    setIsOpen?.(true);
                } else {
                    setSelectedConversationFilter(filter);
                }
            }}
            onKeyDown={(keyboardEvent: KeyboardEvent) => {
                if (keyboardEvent.key === 'Enter') {
                    keyboardEvent.preventDefault();
                }
            }}
            leadingVisual={
                <Box marginRight={1} data-testid="unread-counter">
                    <NotificationCounter isUrgent={hasUrgentUnread}>
                        {unreadCount}
                    </NotificationCounter>
                </Box>
            }
            className={styles['tab']}
        >
            {filterType === 'ALL_ACTIVE' ? 'All' : getShortLabel(filterParticipant)}
        </Tab>
    );
});
